
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Axios from "axios";
import { jwtDecode } from "jwt-decode";
import AkkhorLayout from "../layout/akkhor";
import { URL_LIST_ALL } from "../configs/api";
import Spinner from "../component/Spinner";
import CauHoiRenderer from "./CauHoiRenderer";

const TT = {
    dang_lam: { label: "Đang làm", cls: "badge-info" },
    cho_cham: { label: "Đã nộp - chờ chấm", cls: "badge-warning" },
    da_cham: { label: "Đã nộp - đã chấm", cls: "badge-success" },
};

export default function TheoDoiLamBai() {
    const { id } = useParams(); // dethi_id
    const { user } = useSelector((state) => state.auth);
    const [dethi, setDethi] = useState(null);
    const [cauhoi, setCauHoi] = useState([]);
    const [hocsinh, setHocSinh] = useState([]); // [{user_id, fullname, code, trang_thai, answers, so_da_lam, ...}]
    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState(false);
    const [expand, setExpand] = useState({}); // user_id -> bool (xem chi tiết đáp án)
    const socketRef = useRef(null);
    const closedRef = useRef(false);

    // Cập nhật 1 học sinh trong danh sách (merge theo user_id)
    const upsertHS = useCallback((info) => {
        setHocSinh((prev) => {
            const idx = prev.findIndex((h) => String(h.user_id) === String(info.user_id));
            if (idx === -1) return [{ ...info }, ...prev];
            const next = prev.slice();
            next[idx] = { ...next[idx], ...info };
            return next;
        });
    }, []);

    // Snapshot ban đầu
    useEffect(() => {
        if (!(id > 0)) return;
        setLoading(true);
        Axios.get(URL_LIST_ALL + "learninghub_monitor?dethi_id=" + id, user.config)
            .then((res) => {
                setLoading(false);
                if (res.data.status === "success") {
                    const d = res.data.data;
                    setDethi(d.dethi);
                    setCauHoi(d.list_cauhoi || []);
                    setHocSinh(d.hocsinh || []);
                } else console.log(res.data.message);
            })
            .catch((err) => { console.log(err); setLoading(false); });
    }, [user, id]);

    // WebSocket realtime
    useEffect(() => {
        closedRef.current = false;
        const connect = () => {
            const ws = new WebSocket("wss://socket.bhsgroup.vn");
            ws.onopen = () => setConnected(true);
            ws.onmessage = (evt) => {
                if (!evt.data) return;
                try {
                    const msg = jwtDecode(evt.data);
                    if (msg && msg.channel === 'bhs_socket' && msg.data && msg.data.action === 'learning_monitor'
                        && String(msg.data.dethi_id) === String(id)) {
                        upsertHS(msg.data);
                    }
                } catch (e) { /* ignore */ }
            };
            ws.onclose = () => {
                setConnected(false);
                if (!closedRef.current) setTimeout(connect, 1500); // tự kết nối lại
            };
            socketRef.current = ws;
        };
        connect();
        return () => { closedRef.current = true; if (socketRef.current) socketRef.current.close(); };
    }, [id, upsertHS]);

    return (
        <AkkhorLayout idrole="learninghub_bailam">
            <div className="breadcrumbs-area">
                <ul>
                    <li><Link to="/">Trang chủ</Link></li>
                    <li><Link to="/learninghub_dethi">Bài kiểm tra / Bài tập</Link></li>
                    <li>Theo dõi làm bài</li>
                </ul>
            </div>

            <div className="card height-auto mt-2">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3 className="m-0">Theo dõi realtime: {dethi && dethi.title}</h3>
                        <span className={"badge " + (connected ? "badge-success" : "badge-secondary")}>
                            <span className="fas fa-circle mr-1" style={{ fontSize: 8 }}></span>{connected ? 'Đang kết nối' : 'Mất kết nối'}
                        </span>
                    </div>
                    {loading && <div className="w-100 text-center"><Spinner /></div>}
                    {!loading && hocsinh.length === 0 && <div className="alert alert-info">Chưa có thí sinh nào mở bài.</div>}

                    {hocsinh.map((h) => {
                        const tt = TT[h.trang_thai] || { label: h.trang_thai, cls: 'badge-secondary' };
                        const open = !!expand[h.user_id];
                        return (
                            <div className="border rounded mb-2" key={h.user_id}>
                                <div className="d-flex align-items-center p-2" style={{ cursor: 'pointer' }} onClick={() => setExpand((p) => ({ ...p, [h.user_id]: !p[h.user_id] }))}>
                                    <strong className="mr-2">{h.fullname}</strong>
                                    {h.code && <span className="text-muted mr-2">({h.code})</span>}
                                    <span className={"badge mr-2 " + tt.cls}>{tt.label}</span>
                                    {h.trang_thai === 'dang_lam'
                                        ? <span className="text-muted">Đã làm {h.so_da_lam || 0}/{cauhoi.length} câu</span>
                                        : (h.tong_diem != null && <span className="text-success">Điểm: {h.tong_diem}/{dethi && dethi.tong_diem}</span>)}
                                    <span className={"ml-auto fas " + (open ? "fa-angle-up" : "fa-angle-down")}></span>
                                </div>

                                {open &&
                                    <div className="p-3 border-top bg-light">
                                        {h.answers
                                            ? cauhoi.map((c, idx) => {
                                                const ans = h.answers[c.cauhoi_id];
                                                return (
                                                    <div className="mb-3" key={c.cauhoi_id}>
                                                        <div className="mb-1"><strong>Câu {idx + 1}.</strong> <span dangerouslySetInnerHTML={{ __html: c.title }} /></div>
                                                        {ans === undefined || ans === null || ans === ''
                                                            ? <em className="text-muted">(chưa trả lời)</em>
                                                            : <CauHoiRenderer cauhoi={c} value={ans} readOnly={true} correct={c.dapan} />}
                                                    </div>
                                                );
                                            })
                                            : <em className="text-muted">Chưa có dữ liệu trả lời.</em>}
                                    </div>}
                            </div>
                        );
                    })}
                </div>
            </div>
        </AkkhorLayout>
    );
}
